/**
 * Camera rig: named camera views plus a timed blend between them.
 *
 * Extracted from SceneRenderer (LOCKED-DESIGN 18.8) so the locked second
 * fixed world-upright chamber view (2.2 / 18.5) has a home. Phase 0 registers
 * only 'arena-follow', which reproduces the legacy follow math verbatim;
 * fixed views and blendTo are the API surface Phase 2 builds on.
 *
 * Screen-relative controls contract: every view carries the yaw that defines
 * the input basis while it is active, and `inputYaw` always reports the blend
 * DESTINATION's yaw so controls are never tied to a view the player is
 * leaving. The locked chamber view is world-upright with the arena yaw, so
 * W stays up-screen through the blend (2.2). Render-only either way — the
 * rig never touches the deterministic kernel.
 */
import * as THREE from "three";
import type { Vec3 } from "../world/ArenaDefinition.js";

/** Smoothed follow that tracks a moving focus (the player) — today's gameplay camera. */
export interface FollowViewConfig {
  kind: "follow";
  /** input-basis yaw (radians about +Y); the authored offset is rotated by this */
  yaw: number;
  /** camera offset from the follow target, authored pre-yaw */
  offset: Vec3;
  lookHeight: number;
  northBias: number;
  followRate: number;
  lookahead: number;
  fov: number;
}

/** Static world-upright pose (the Phase 2 chamber view). */
export interface FixedViewConfig {
  kind: "fixed";
  /** input-basis yaw while this view is active */
  yaw: number;
  position: Vec3;
  target: Vec3;
  fov: number;
}

export type CameraViewConfig = FollowViewConfig | FixedViewConfig;

/** Per-frame focus the follow views track (player position + planar velocity). */
export interface CameraFocus {
  x: number;
  z: number;
  vx: number;
  vz: number;
}

/** Resolved camera pose for one frame. Vectors are rig-owned scratch: copy, don't retain. */
export interface CameraPose {
  readonly position: THREE.Vector3;
  readonly target: THREE.Vector3;
  readonly fov: number;
}

interface MutablePose {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
}

interface RegisteredView {
  config: CameraViewConfig;
  /** follow smoothing state (world-space); starts at origin until snapped */
  followTarget: THREE.Vector3;
  /** authored offset rotated into world space by the view yaw, computed once */
  offsetWorld: THREE.Vector3;
}

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Inset from every enclosure face the camera is allowed to reach (metres).
 * Exported so SceneRenderer's post-shake re-clamp uses the SAME skin.
 */
export const CAMERA_BOUNDS_SKIN = 0.9;
/**
 * Breach distance at which the look target has fully transferred from the
 * biased follow target onto the player's own ground position. Chosen so the
 * transfer completes just as the south wall clamp saturates (~10 m breach at
 * the south edge) while a 1–2 m graze barely moves the aim.
 */
const TARGET_PULL_FULL_M = 6;

/**
 * Clamp a point into `[min + skin, max - skin]` PER AXIS, in place.
 *
 * Exported so SceneRenderer can re-apply it after screenshake/kick push the
 * camera back out (the rig's clamp only owns the pose it hands over).
 *
 * Per-axis, deliberately NOT a boom shorten along the offset ray: pulling the
 * camera down the boom to the first in-bounds `t` collapses the height (at the
 * south edge the largest legal t is 0.370 → the camera drops to y 6.36 and the
 * player ends up 42.3° off axis against a 28° vertical half-FOV, i.e. off
 * screen). Sliding along the wall keeps the camera high and the player inside
 * the frustum. Per-axis is also robust to a follow target that is itself
 * outside the box — at player z −18 the target sits at z −23.5 while the
 * camera at z −7 is legitimately inside, where a ray/slab test would
 * misbehave and this clamp is correctly a no-op.
 */
export function clampIntoBounds(p: THREE.Vector3, min: Vec3, max: Vec3, skin: number): void {
  p.x = Math.min(Math.max(p.x, min[0] + skin), max[0] - skin);
  p.y = Math.min(Math.max(p.y, min[1] + skin), max[1] - skin);
  p.z = Math.min(Math.max(p.z, min[2] + skin), max[2] - skin);
}

/**
 * Input-basis yaw for a fixed view, DERIVED from its pose: the yaw whose
 * up-screen direction (−sin yaw, −cos yaw — the InputSampler W mapping)
 * matches the camera's ground-plane forward. Deriving instead of authoring a
 * separate yaw makes it impossible for a fixed view's look direction and its
 * movement basis to disagree (LOCKED-DESIGN 2.2 screen-relative controls).
 */
export function fixedViewYaw(position: Vec3, target: Vec3): number {
  return Math.atan2(-(target[0] - position[0]), -(target[2] - position[2]));
}

const makePose = (): MutablePose => ({ position: new THREE.Vector3(), target: new THREE.Vector3(), fov: 0 });

export class CameraRig {
  private readonly views = new Map<string, RegisteredView>();
  private activeName: string | null = null;
  private blendName: string | null = null;
  private blendElapsed = 0;
  private blendDuration = 0;

  /** enclosure the follow views may not leave; null = unbounded (legacy). */
  private bounds: { min: Vec3; max: Vec3 } | null = null;

  // rig-owned scratch
  private readonly desired = new THREE.Vector3();
  private readonly raw = new THREE.Vector3();
  private readonly focusPoint = new THREE.Vector3();
  private readonly poseFrom = makePose();
  private readonly poseTo = makePose();
  private readonly pose = makePose();

  /**
   * Containment box for FOLLOW views (PLAYTEST F6). `null` — the default —
   * reproduces the unbounded legacy pose byte for byte. Fixed views are never
   * clamped: those poses are authored and sightline-verified (18.13).
   */
  setBounds(bounds: { min: Vec3; max: Vec3 } | null): void {
    this.bounds = bounds;
  }

  /** Register a named view. The first registered view becomes active. */
  addView(name: string, config: CameraViewConfig): void {
    if (this.views.has(name)) throw new Error(`CameraRig view '${name}' is already registered`);
    const view: RegisteredView = {
      config,
      followTarget: new THREE.Vector3(),
      offsetWorld:
        config.kind === "follow"
          ? new THREE.Vector3().fromArray(config.offset).applyAxisAngle(UP, config.yaw)
          : new THREE.Vector3(),
    };
    this.views.set(name, view);
    if (this.activeName === null) this.activeName = name;
  }

  /** Hard cut to a view; cancels any blend in flight. */
  setActiveView(name: string): void {
    this.requireView(name);
    this.activeName = name;
    this.blendName = null;
    this.blendElapsed = 0;
    this.blendDuration = 0;
  }

  /**
   * Timed blend to a view. Non-positive duration is a hard cut. A blend that
   * interrupts a blend restarts from whichever view is currently active.
   */
  blendTo(name: string, durationSeconds: number): void {
    this.requireView(name);
    if (durationSeconds <= 0 || name === this.activeName) {
      this.setActiveView(name);
      return;
    }
    this.blendName = name;
    this.blendElapsed = 0;
    this.blendDuration = durationSeconds;
  }

  get activeViewName(): string {
    if (this.activeName === null) throw new Error("CameraRig has no views registered");
    return this.activeName;
  }

  get blending(): boolean {
    return this.blendName !== null;
  }

  /**
   * Whether THIS frame's pose may be re-clamped into the bounds by the renderer
   * after screenshake/kick. False whenever a fixed view contributes to the pose:
   * fixed poses are authored and sightline-verified (18.13) and the rig itself
   * never clamps them, so the renderer must not either — otherwise a fixed view
   * authored within the skin of a wall would be silently displaced every frame
   * while the rig-level exemption test kept passing.
   */
  get poseClampable(): boolean {
    if (this.bounds === null) return false;
    const active = this.views.get(this.activeName ?? "");
    if (active?.config.kind !== "follow") return false;
    const destination = this.blendName === null ? null : this.views.get(this.blendName);
    return destination === null || destination?.config.kind === "follow";
  }

  /**
   * Yaw defining the input basis this frame — the blend destination's yaw, so
   * screen-relative movement (W = up-screen) belongs to the view the player
   * is entering, never the one they are leaving.
   */
  get inputYaw(): number {
    const view = this.views.get(this.blendName ?? this.activeName ?? "");
    if (!view) throw new Error("CameraRig has no views registered");
    return view.config.yaw;
  }

  /** Hard-cut all follow smoothing (portal traversal, respawn) — no smear. */
  snapFollow(x: number, z: number): void {
    for (const view of this.views.values()) {
      if (view.config.kind !== "follow") continue;
      view.followTarget.set(x, view.config.lookHeight, z + view.config.northBias);
    }
  }

  /** Advance smoothing plus any blend, then resolve this frame's camera pose. */
  update(focus: CameraFocus, frameDt: number): CameraPose {
    const active = this.views.get(this.activeName ?? "");
    if (!active) throw new Error("CameraRig has no views registered");
    if (this.blendName === null) {
      this.resolve(active, focus, frameDt, this.pose);
      return this.pose;
    }
    const destination = this.views.get(this.blendName)!;
    this.blendElapsed += frameDt;
    if (this.blendElapsed >= this.blendDuration) {
      // blend complete: land exactly on the destination resolution
      this.activeName = this.blendName;
      this.blendName = null;
      this.resolve(destination, focus, frameDt, this.pose);
      return this.pose;
    }
    // both endpoints keep simulating so the blend tracks live poses
    this.resolve(active, focus, frameDt, this.poseFrom);
    this.resolve(destination, focus, frameDt, this.poseTo);
    const t = this.blendElapsed / this.blendDuration;
    const e = t * t * (3 - 2 * t); // smoothstep: gentle ease at both ends
    this.pose.position.lerpVectors(this.poseFrom.position, this.poseTo.position, e);
    this.pose.target.lerpVectors(this.poseFrom.target, this.poseTo.target, e);
    this.pose.fov = this.poseFrom.fov + (this.poseTo.fov - this.poseFrom.fov) * e;
    return this.pose;
  }

  /**
   * Legacy SceneRenderer follow math, preserved verbatim for zero framing
   * drift, plus the F6 containment pass when bounds are set.
   *
   * Containment clamps the OUTPUT pose only — `view.followTarget` is never
   * written back, or the smoother would wind up against the wall and pop when
   * the player walks away from it. Both blend endpoints run through here, so a
   * blend can never interpolate outside the box either.
   */
  private resolve(view: RegisteredView, focus: CameraFocus, frameDt: number, out: MutablePose): void {
    const config = view.config;
    if (config.kind === "fixed") {
      // authored, sightline-verified pose (18.13): never clamped
      out.position.fromArray(config.position);
      out.target.fromArray(config.target);
      out.fov = config.fov;
      return;
    }
    // follow target: focus position + a touch of velocity lookahead
    this.desired.set(
      focus.x + focus.vx * config.lookahead,
      config.lookHeight,
      focus.z + focus.vz * config.lookahead + config.northBias,
    );
    const k = 1 - Math.exp(-config.followRate * frameDt);
    view.followTarget.lerp(this.desired, k);
    const raw = this.raw.copy(view.followTarget).add(view.offsetWorld);
    out.position.copy(raw);
    out.target.copy(view.followTarget);
    out.fov = config.fov;
    if (this.bounds === null) return;
    clampIntoBounds(out.position, this.bounds.min, this.bounds.max, CAMERA_BOUNDS_SKIN);
    const breach = raw.distanceTo(out.position);
    if (breach === 0) return;
    // Target pull: as the clamp bites, the aim slides off the north-biased
    // follow target and onto the player's OWN ground position at look height
    // (no northBias, no lookahead), so the camera tilts down onto them instead
    // of staring past them into the wall it just slid along. This is what
    // keeps the player — and the near-wall drop zones — inside the frustum.
    const pull = Math.min(1, breach / TARGET_PULL_FULL_M);
    this.focusPoint.set(focus.x, config.lookHeight, focus.z);
    out.target.lerp(this.focusPoint, pull);
  }

  private requireView(name: string): void {
    if (!this.views.has(name)) throw new Error(`CameraRig view '${name}' is not registered`);
  }
}
